import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { usePostHog } from "posthog-js/react";

import KnowledgeBaseEntryFields from "components/KnowledgeBaseEntryFields";
import Loading from "components/Loading";
import NotFound from "components/NotFound/NotFound";
import PageHeader from "components/PageHeader";
import RichText from "components/RichText";
import { PortalContent } from "layouts/PortalLayout";
import { CARE_TEAM_EMAIL, PHONE } from "constants/at-need";
import {
  KNOWLEDGE_BASE_TAGS,
  entriesInCategory,
  uncategorisedEntries,
  useKnowledgeBase,
  type KnowledgeBaseEntry,
} from "hooks/useKnowledgeBaseAPI";
import {
  answersBackLink,
  knowledgeBaseSubjectFromParams,
  knowledgeBaseSubjectProperties,
} from "helpers/knowledge-base";
import { formatPhoneNumber } from "helpers/phone";

// This page is the frequently-asked surface. Other tags exist for answers that
// belong somewhere more specific and would only clutter a reading page.
const PAGE_TAGS = [KNOWLEDGE_BASE_TAGS.faq] as const;

function KnowledgeBaseFrame({ children }: { children: ReactNode }) {
  return <PortalContent>{children}</PortalContent>;
}

function EntryDisclosure({
  entry,
  onOpen,
}: {
  entry: KnowledgeBaseEntry;
  onOpen: () => void;
}) {
  return (
    <Disclosure
      as="div"
      // `id` makes each answer linkable, and `entry_key` is stable across
      // content edits precisely so a link shared today still lands tomorrow.
      id={entry.entry_key}
      className="scroll-mt-24"
    >
      {({ open }) => (
        <>
          <DisclosureButton
            onClick={() => {
              if (!open) onOpen();
            }}
            className="group flex w-full items-start justify-between gap-4 px-5 py-4 text-left"
          >
            <h3 className="font-serif text-lg leading-snug text-soil">
              {entry.title}
            </h3>
            <ChevronDownIcon
              className={`mt-0.5 h-5 w-5 shrink-0 text-plum transition-all duration-200 group-hover:text-evergreen ${
                open ? "rotate-180" : ""
              }`}
              aria-hidden="true"
            />
          </DisclosureButton>
          <DisclosurePanel className="px-5 pb-5">
            <RichText html={entry.body_html} className="text-base text-soil" />
            <KnowledgeBaseEntryFields fields={entry.fields} />
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
}

export default function KnowledgeBasePage() {
  const { caseId, policyId } = useParams<{
    caseId?: string;
    policyId?: string;
  }>();
  const subject = useMemo(
    () => knowledgeBaseSubjectFromParams({ caseId, policyId }),
    [caseId, policyId],
  );
  const subjectKey = subject ? `${subject.kind}:${subject.id}` : null;
  const posthog = usePostHog();
  const { categories, entries, isLoading, error } = useKnowledgeBase(
    subject,
    PAGE_TAGS,
  );
  const uncategorised = uncategorisedEntries(entries);

  // Once per case or policy, not once per revalidation: this event is how we find out
  // whether the knowledge base is actually reducing calls, and SWR refetching
  // would inflate it.
  const viewReported = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading || error || !subject || !subjectKey) return;
    if (viewReported.current === subjectKey) return;
    viewReported.current = subjectKey;
    posthog?.capture("knowledge_base_page_viewed", {
      ...knowledgeBaseSubjectProperties(subject),
      entry_count: entries.length,
    });
  }, [subject, subjectKey, entries.length, error, isLoading, posthog]);

  if (isLoading) {
    return (
      <KnowledgeBaseFrame>
        <Loading message="Loading answers..." />
      </KnowledgeBaseFrame>
    );
  }

  if (error) {
    return (
      <KnowledgeBaseFrame>
        <div className="my-16 flex items-center justify-center px-3">
          <NotFound
            title="We couldn't load these answers"
            message="Please try again in a moment, or call us and we'll answer your questions directly."
          />
        </div>
      </KnowledgeBaseFrame>
    );
  }

  return (
    <KnowledgeBaseFrame>
      <div className="flex flex-col gap-8">
        <PageHeader
          backLink={subject ? answersBackLink(subject) : undefined}
          title="Questions we hear most often"
          description={
            <>
              If you don&apos;t see what you&apos;re looking for, your Care
              Specialist can help — call us or email{" "}
              <a
                href={`mailto:${CARE_TEAM_EMAIL}`}
                className="font-semibold text-evergreen underline underline-offset-2"
              >
                {CARE_TEAM_EMAIL}
              </a>
              .
            </>
          }
        />

        {categories.length === 0 && uncategorised.length === 0 ? (
          <p className="text-base text-evergreen">
            We don&apos;t have any answers to show here yet.
          </p>
        ) : (
          categories.map((category) => (
            <section key={category.slug} className="flex flex-col gap-3">
              <h2 className="text-xs font-bold uppercase tracking-[.14em] text-evergreen">
                {category.label}
              </h2>
              <div className="overflow-hidden rounded-xl border border-oat-200 bg-white shadow-sm">
                {entriesInCategory(entries, category.slug).map(
                  (entry, index) => (
                    <div key={entry.entry_key}>
                      {index > 0 && <div className="border-t border-oat-100" />}
                      <EntryDisclosure
                        entry={entry}
                        onOpen={() =>
                          posthog?.capture("knowledge_base_entry_expanded", {
                            ...(subject &&
                              knowledgeBaseSubjectProperties(subject)),
                            entry_key: entry.entry_key,
                            category: entry.category,
                          })
                        }
                      />
                    </div>
                  ),
                )}
              </div>
            </section>
          ))
        )}

        {uncategorised.length > 0 && (
          <section className="flex flex-col gap-3">
            <div className="overflow-hidden rounded-xl border border-oat-200 bg-white shadow-sm">
              {uncategorised.map((entry, index) => (
                <div key={entry.entry_key}>
                  {index > 0 && <div className="border-t border-oat-100" />}
                  <EntryDisclosure
                    entry={entry}
                    onOpen={() =>
                      posthog?.capture("knowledge_base_entry_expanded", {
                        ...(subject && knowledgeBaseSubjectProperties(subject)),
                        entry_key: entry.entry_key,
                        category: entry.category,
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="rounded-3xl bg-oat p-6 text-center">
          <p className="text-base text-evergreen">Still need help?</p>
          <p className="text-base text-evergreen">
            Call us at{" "}
            <a
              href={`tel:${PHONE}`}
              className="font-semibold underline underline-offset-2"
            >
              {formatPhoneNumber(PHONE)}
            </a>
          </p>
        </div>
      </div>
    </KnowledgeBaseFrame>
  );
}
